import KnotchDate from 'helpers/KnotchDate';
import { snakeCase } from 'lodash';
import moment from 'moment';

moment.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    ss: '%d seconds',
    m: 'a min',
    mm: '%dmins',
    h: 'an hour',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    w: '1w',
    ww: '%dw',
    M: '1m',
    MM: '%dm',
    y: '1y',
    yy: '%dy',
  },
});

/**
 * @desc Formats time in secs to hours, mins, secs
 * @param  {Number} secs The number of seconds to convert.
 * @return {String} Time in hours, mins, secs.
 */

export function formatTime(secs) {
  if (secs === undefined || secs === null) {
    return '0s';
  }

  const d = Math.floor(secs / (3600 * 24));
  secs -= d * 3600 * 24;
  const h = Math.floor(secs / 3600);
  secs -= h * 3600;
  const m = Math.floor(secs / 60);
  secs -= m * 60;
  const tmp = [];

  (d) && tmp.push(`${d}d`);

  (d || h) && tmp.push(`${h}h`);

  (d || h || m) && tmp.push(`${m}m`);

  tmp.push(`${Math.floor(secs)}s`);

  return tmp.join(' ');
}

export function wrapBg(img) {
  return img ? { backgroundImage: `url('${img}')` } : null;
}

export const isObjectEmpty = (obj) => {
  let key;
  for (key in obj) {
    if (obj.hasOwnProperty(key)) { return false; }
  }
  return true;
};

/**
 * Removes the keys inside the fields array from the given object
 * @param {Object} obj
 * @param {Array} fields
 * @return {Object}
 */
export const removeKeysFromObject = (obj, fields) => Object.keys(obj)
    .filter(key => (!fields.includes(parseInt(key, 10))))
    .reduce((newObj, key) => ({
      ...newObj,
      [key]: obj[key],
    }), {});

export const toQueryString = (obj) => {
  const parts = [];
  for (const i in obj) {
    if (obj.hasOwnProperty(i) && obj[i] && encodeURIComponent(obj[i]).length) {
      parts.push(`${encodeURIComponent(i)}=${encodeURIComponent(obj[i])}`);
    }
  }
  return parts.join('&');
};

export const toQueryObject = (str) => {
  // remove any preceding url and split
  str = str.substring(str.indexOf('?') + 1).split('&');
  const params = {};
  let pair;
  const d = decodeURIComponent;
  // march and parse
  for (let i = str.length - 1; i >= 0; i--) {
    pair = str[i].split('=');
    let key = d(pair[0]);
    const value = d(pair[1]);

    if (key.indexOf('[]') !== -1) {
      key = key.replace('[]', '');
      if (params[key]) {
        params[key].push(value);
      } else {
        params[key] = [value];
      }
    } else {
      params[d(pair[0])] = d(pair[1] || '');
    }
  }

  return params;
};

/**
 * Return the percentual representation of a number.
 * @param  {Number} number        The number to convert.
 * @param  {Number} [decimals=1]  Maximum decimals to represent.
 * @param {Boolean} withSymbol    Whether you'd like the % to be returned.
 * @return {String}               Given number in percentual + %.
 * @example
 * inPercent(0.1);
 * // Returns '10%'
 * inPercent(0.0001);
 * // Returns '0%'
 * inPercent(0.10001, 3);
 * // Returns '10.001%'
 */
export const inPercent = (number, decimals = 1, withSymbol = true) => {
  const roundFactor = Math.pow(10, decimals);
  const roundedNumber = (Math.round(number * roundFactor * 100)) / roundFactor;
  if (withSymbol) return `${roundedNumber}%`;
  return `${roundedNumber}`;
};

/**
 * Return the percentual representation of a number compared
 * to the total number passed in. Formula: Number/Total = P%
 * Always returns a whole number between 1-100
 * @param  {Number} number        The number to convert.
 * @param  {Number} total         Total aggregate. Must be >= number.
 * @param {Boolean} withSign      Whether you'd like the % to be returned.
 * @return {String}               Given number in percentual + %.
 * @example
 * percentageOf(12, 60);
 * // Returns '20%'
 * percentageOf(10, 100, false);
 * // Returns '10'
 * percentageOf(80, 3920)
 * // Returns '2%'
 */
export const percentageOf = (number, total, withSign = true) => {
  const n = number / total;
  if (!n) return inPercent((0), 1, withSign); // check for NaN
  return inPercent((n), 1, withSign);
};

export const roundDecimal = (number, decimals = 1) => {
  const roundFactor = Math.pow(10, decimals);
  return (Math.round(number * roundFactor * 100)) / roundFactor;
};

export const getHSL = string => string.match(/\d+/g).map(Number);
export const linSpace = n => [...Array(n).keys()].map(i => i / n);

/**
 * Return a spectrum of N colors between two given colors.
 * @param  {String} from    Starting color in HSL.
 * @param  {String} to      Ending color in HSL.
 * @param  {Number} colors  Number of colors to return.
 * @return {Array.String}   Array of HSL colors.
 * @example
 * spectrum({ from: 'hsl(344, 97%, 60%)', to: 'hsl(390, 94%, 99%)', colors: 5 });
 * // Returns
 * // [ 'hsl(344, 97%, 60%)',
 * //   'hsl(353.2, 96.4%, 67.8%)',
 * //   'hsl(362.4, 95.8%, 75.6%)',
 * //   'hsl(371.6, 95.2%, 83.4%)',
 * //   'hsl(380.8, 94.6%, 91.2%)' ]
 */
export const spectrum = ({ from, to, colors }) => {
  const [fH, fS, fL] = getHSL(from);
  const [tH, tS, tL] = getHSL(to);
  const [dH, dS, dL] = [tH - fH, tS - fS, tL - fL];

  return linSpace(colors)
    .map((i) => {
      const iH = fH + (dH * i);
      const iS = fS + (dS * i);
      const iL = fL + (dL * i);
      return `hsl(${iH}, ${iS}%, ${iL}%)`;
    });
};

/**
 * Return a comma formatted string of a number
 * @param  {Number} number        The number to convert with commas
 * @param  {String} words         Word to append to string
 * @return {String}               Concatenated formatted string
 * @example
 * numberWithCommas(11234);
 * // Returns '11,234'
 * numberWithCommas(90000000)
 * // Returns '90,000,000'
 */
export const numberWithCommas = x => x && Math.floor(x).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * Return a curried function that returns takes a value, formats it and appends words
 * @param  {String} words         Word(s) to append after val is formatted
 * @param  {Function} formatter   Formatting callback function
 * @return {Fnc}                  Curried function
 * @example
 * const formatter = formatterWithWord('Shares', numberWithCommas);
 * formatter(11234);
 * // Returns '11,234 Shares'
 */
export const formatterWithWord = (words, formatter) => value => {
  const formattedValue = formatter(value);
  const isValid = formattedValue !== null;
  return isValid ? `${formatter(value)} ${words}` : `-- ${words}`;
};

// ... turns a {key:value} object in a [{name:key, value: value}] array
export const objectToSortedArray = (object) => {
  if (object) {
    return Object.keys(object)
    .map(key => ({ name: key, value: object[key] }))
    .sort((a, b) => b.value - a.value);
  }
  return [];
};

/**
 * @desc Returns hex codes for sentiment values
 * @param {Number} value The value (from 0 to 20) to convert.
 * @return {String} The Hex string for color for corresponding sentiment value
 */
export const sentimentValueToColor = (value) => {
  const colors = {
    0: '#5ca7ce',
    2: '#74b4d7',
    4: '#93c5de',
    6: '#b3d6e9',
    8: '#d1e7f2',
    10: '#b3b3b3',
    12: '#fe9fa3',
    14: '#fb7576',
    16: '#fc5e6c',
    18: '#fa4b6c',
    20: '#fc376c',
  };
  return colors[value];
};

/**
 * @desc Generate a random whole number within [low, high).
 * @param {Number} low Lower bound (inlcusive)
 * @param  {Number} high Upper bound (exclusive)
 * @return {Number} The desired random number
 */
export const randomIntegerIn = (low, high) => Math.floor(low + (Math.random() * (high - low)));

/**
 * @desc Sort ranges in chosen direction
 * @param {String} unit used for sorting
 * @param {String} direction
 */

export const sortList = (unit, direction) => (a, b) => {
  let dirIndex = 0;
  if (direction === 'ascending') dirIndex = 1;
  if (direction === 'descending') dirIndex = -1;
  return (parseInt(a[unit], 10) - parseInt(b[unit], 10)) * dirIndex;
};

export const sortAlphabetical = (unit, direction) => (a, b) => {
  if (a[unit] && b[unit]) {
    const valA = a[unit].toLowerCase();
    const valB = b[unit].toLowerCase();

    if (direction === 'descending') {
      if (valA < valB) return -1;
      if (valA > valB) return 1;
      return 0;
    }

    if (valA > valB) return -1;
    if (valA < valB) return 1;
    return 0;
  }
  return false;
};

/**
 * @desc Formats a JS Date object into a human readable String
 * @param {Date} date
 * @param {Object} options
 * @return {String}
 */

export function formatDate(date, options = {}) {
  if (options.short) {
    return moment(date).format('MM/DD/YY');
  }
  if (options.medium) {
    return moment(date).format('DD MMM YY');
  }
  if (options.relative) {
    return moment(date).fromNow(true).toString().toUpperCase();
  }

  const dateVal = new KnotchDate(date).parse();
  return `${dateVal.month} ${dateVal.day} ${dateVal.year}`;
}

/**
 * @desc Simple validation functions
 * @param {String} data
 * @return {Boolean}
 */

export const validateString = data => !!(data) && typeof data === 'string' && data.length > 0;

/**
 * @desc Simple validation functions
 * @param {Number} data
 * @return {Boolean}
 */

export const validateNumber = data => !isNaN(data) && typeof data === 'number';

/**
 * @desc Simple validation percentage number between 0 & 1
 * @param {Number} data
 * @return {Boolean}
 */

export const validatePct = data => validateNumber(data) && data >= 0 && data <= 1;

/**
 * @desc vali-Date? Validate date string
 * @param {Date} data
 * @return {Boolean}
 */

export const validateDate = data => !!(data) && !isNaN(Date.parse(data));

/**
 * @desc Check if a URL is relatively correct.
 * @param {Date} data
 * @return {Boolean}
 */

export const validateURL = url => /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?.*$/.test(url);

/**
 * @desc capitalize first letter of string
 */
export const capitalizeString = str => str ? str.charAt(0).toUpperCase() + str.substr(1) : null;

/**
 * @desc snake case to camel case
 */
export const snakeToCamel = (object) => {
  const result = {};
  for (const field in object) {
    let camelField = '';
    const value = typeof object[field] === 'object' ? snakeToCamel(object[field]) : object[field];
    const fieldArray = field.split('_');
    for (let i = 0; i < fieldArray.length; i++) {
      camelField += i === 0 ? fieldArray[i] : capitalizeString(fieldArray[i]);
    }
    result[camelField] = value;
  }
  return result;
};

export const snakeCasify = (data) => {
  const update = {};
  for (const field in data) {
    update[snakeCase(field)] = data[field];
  }
  return update;
};

/**
 * @desc converts 12345 to 12.3k
 */
export const roundedNumber = (num, fixed) => {
  if (num === null || num === undefined) { return null; }
  if (num === 0) { return '0'; }
  fixed = (!fixed || fixed < 0) ? 0 : fixed;
  const b = (num).toPrecision(2).split('e');
  const k = b.length === 1 ? 0 : Math.floor(Math.min(b[1].slice(1), 14) / 3);
  const c = k < 1 ? num.toFixed(0 + fixed) : (num / Math.pow(10, k * 3)).toFixed(1 + fixed);
  const d = c < 0 ? c : Math.abs(c);
  const e = d + ['', 'k', ' Million', 'b', 't'][k];
  return e;
};

/**
 * @desc Helper to get sum of array of data
 */
export const sumArray = (array, unit = 'value') => array.reduce((acc, curr) => acc + curr[unit], 0);

/**
 * @desc function to sort accounts
 * @param {*} a bool
 * @param {*} b bool
 */
export const sortAccounts = (a, b) => {
  if (a.active === b.active) {
    if (a.name < b.name) {
      return -1;
    }
    return 1;
  } else if (a.active) {
    return -1;
  }
  return 1;
};

/**
 *
 * @desc returns function to use in arr.sort to sort by an object value
 * @param {*} property string
 */
export const sortBy = (property, order) => (a, b) => {
  const descending = order === 'DESC';
  if (a[property] < b[property]) { return descending ? 1 : -1; }
  if (a[property] > b[property]) { return descending ? -1 : 1; }
  return 0;
};

/**
 * Returns a {key: [...values]} pair for objects with the same key
 * @param {Object} collection
 * @param {String} property
 * @return {Object}
 */
export const groupBy = (collection, property) => {
  let i = 0;
  let val;
  let index;
  const values = [];
  const result = [];
  for (; i < collection.length; i++) {
    val = collection[i][property];
    index = values.indexOf(val);
    if (index > -1) { result[index].push(collection[i]); } else {
      values.push(val);
      result.push([collection[i]]);
    }
  }
  return result;
};

/**
 * Splits a flat array in an array of arrays of size chunkSize
 * ex: chunkArray([1, 2, 3], 1) => [[1], [2], [3]]
 * @param {[*]} arr the array that will be split into chunks
 * @param {int} chunkSize the size of chunks to be split into
 */
export const chunkArray = (arr, chunkSize = 1) => {
  const newArray = [];
  const tmp = [...arr];
  if (chunkSize <= 0) return newArray;
  while (tmp.length) newArray.push(tmp.splice(0, chunkSize));
  return newArray;
};

export const articleLengthToWordCount = {
  short: '< 300 Words',
  medium: '> 300 Words',
  long: '> 1000 Words',
};

/**
 * @desc Takes an array and removes any values non-unique
 * @param {Array} arr the array that contains duplicates
 * @return {Array} new array with unique values
 * @example removeDuplicatesInArray([1, 2, 3, 2, 2, 3, 4, 5]) => [1, 2, 3, 4, 5]
 */
export const removeDuplicatesInArray = (arr) =>
  arr.reduce((acc, curr, i, org) => {
    if (org.indexOf(curr) === i) {
      return [...acc, curr];
    }
    return acc;
  }, []);

/**
 * @desc Truncates a string to a specific length and adds ellipsis
 * @param {String} str
 * @param {Number} length
 * @return {String}
 */
export const truncateStringWithEllipsis = (str = '', length = 1000) => {
  if (!str) {
    return null;
  }

  if (str.length > length) {
    return `${str.substring(0, length)}...`;
  }

  return str;
};
